import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

const FAILURE_REASON_LABEL = {
  ITEM_CANCELLED: 'Item Cancelled',
  LABEL_NOT_FOUND: 'Label Not Found',
  PRINT_FAILED: 'Print Failed',
  ITEM_LOCKED_FOR_PHYSICAL_COUNT: 'Locked for Physical Count',
  PRODUCT_ID_UNRESOLVED: 'Product ID Unresolved',
  DEFAULT_NDC_UNRESOLVED: 'Default NDC Unresolved',
  // POM-6010: commitBulkLabelLotNdc refusal reasons. A reason with no entry here renders as a
  // bare "Request failed", which is the regression QA re-opened POM-5933 for.
  LINE_ITEM_NOT_FOUND: 'Line Item Not Found',
  FILL_NOT_FOUND: 'Fill Not Found',
  ALREADY_DISPENSED: 'Already Dispensed',
  LOT_NOT_AVAILABLE: 'Lot No Longer Available',
  LOT_EXPIRED: 'Lot Expired',
  NDC_NOT_FOR_PRODUCT: 'NDC Not Valid For This Product',
  QUANTITY_MISMATCH: 'Quantity Does Not Match Fill',
  UPDATE_FAILED: 'Update Failed',
  // POM-6121: commitBulkLabelLotNdc can now refuse ONE hand-recorded row while its siblings
  // commit, so each of these has to have a label here. Without one the row renders as a bare
  // "Request failed", which is the regression QA re-opened POM-5933 for.
  //
  // These are the mutation's per-row BulkLabelLotCommitFailureReason values, NOT the
  // INVENTORY_* ApolloError codes resolveOrCreateLotForFill throws internally — core maps
  // those onto these before it returns, so an INVENTORY_* key here would never match.
  MANUAL_LOT_NDC_REQUIRED: 'NDC Not In Inventory',
  MANUAL_LOT_BUD_AMBIGUOUS: 'Lot Number Repeats Under Different Expiry Dates',
  MANUAL_LOT_HAS_AVAILABLE_STOCK: 'Lot Still Has Stock — Select It Instead',
  // POM-6119: the typed number already belongs to a different drug. Says the vial rather than the
  // lot, because that is what the operator has to go and look at — nothing here can be corrected
  // by editing the number if the number is right and the drug is wrong.
  MANUAL_LOT_FOREIGN_NDC: 'Lot Belongs To Another Drug — Check The Vial',
  // POM-6535: the batch asks the lot for more than it holds. The review's refused panel words it
  // per row (lot, facility, shortfall); this is the fallback when those details are missing.
  INSUFFICIENT_LOT_QUANTITY_IN_BATCH: 'Insufficient Quantity In Lot',
};

export { FAILURE_REASON_LABEL };

const PrintResultSummary = ({ printResult, isError }) => {
  const theme = useTheme();
  if (!printResult) return null;

  const failedResults = printResult.results.filter((r) => !r.success);
  // POM-6010: items that filled successfully but from a different lot than the printed label
  // carries. Not a failure — the item is filled — but the vial in hand is mislabelled, so it is
  // called out separately rather than buried with the refusals.
  const divergedResults = printResult.results.filter((r) => r.success && r.lotDiverged);
  const totalOrders = printResult.successCount + printResult.failedCount;

  const accentColor = isError
    ? theme.palette.error.main
    : theme.palette.warning.warningColour || theme.palette.warning.main;

  return (
    <Box sx={{ py: 3, px: 3, textAlign: 'center' }}>
      {/* Icon */}
      <WarningAmberIcon sx={{ fontSize: 38, color: accentColor, mb: 1 }} />

      {/* Headline */}
      <Typography variant='h6' sx={{ fontWeight: 700, color: accentColor, mb: 0.5 }}>
        {printResult.failedCount} {printResult.failedCount === 1 ? 'order' : 'orders'} could not be
        processed
      </Typography>

      {/* Subtitle */}
      <Typography variant='body2' sx={{ color: '#16a34a', fontWeight: 500 }}>
        {printResult.successCount} of {totalOrders} orders processed successfully
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Failed Orders Table */}
      {failedResults.length > 0 && (
        <TableContainer
          sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 1.5 }}
        >
          <Table size='small' stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: '35%',
                    backgroundColor: '#f9fafb',
                    color: '#374151',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  Order ID
                </TableCell>
                <TableCell
                  align='left'
                  sx={{
                    width: '30%',
                    backgroundColor: '#f9fafb',
                    color: '#374151',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  Rx Number
                </TableCell>
                <TableCell
                  align='left'
                  sx={{
                    width: '35%',
                    backgroundColor: '#f9fafb',
                    color: '#374151',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  Reason
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {failedResults.map((r, idx) => (
                <TableRow
                  key={r.lineItemId}
                  sx={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa' }}
                >
                  <TableCell
                    sx={{
                      width: '35%',
                      py: '10px',
                      color: '#111827',
                      fontWeight: 500,
                      fontSize: '0.8125rem',
                    }}
                  >
                    {r.orderId || r.lineItemId}
                  </TableCell>
                  <TableCell
                    align='left'
                    sx={{
                      width: '30%',
                      py: '10px',
                      color: '#111827',
                      fontWeight: 500,
                      fontSize: '0.8125rem',
                    }}
                  >
                    {r.rxNumber || '—'}
                  </TableCell>
                  <TableCell
                    align='left'
                    sx={{ width: '35%', py: '10px', color: '#6b7280', fontSize: '0.8125rem' }}
                  >
                    {FAILURE_REASON_LABEL[r.reason] || r.reason}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {divergedResults.length > 0 && (
        <Box sx={{ mt: 3, textAlign: 'left' }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5 }}>
            {`${divergedResults.length} item(s) were filled from a different lot than the printed label shows`}
          </Typography>
          <Typography variant='body2' sx={{ color: '#6b7280', mb: 1 }}>
            The lot was not reserved when the label printed, so fulfilment re-picked it. Re-print
            these labels before dispensing.
          </Typography>
          {divergedResults.map((r) => (
            <Typography key={r.lineItemId} variant='body2' sx={{ color: '#374151' }}>
              {`${r.orderId || r.lineItemId}${r.rxNumber ? ` · ${r.rxNumber}` : ''} — label shows ${
                (r.labelLotNumbers || []).join(', ') || '(no lot)'
              }, filled from ${(r.allocatedLotNumbers || []).join(', ') || '(no lot)'}`}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

PrintResultSummary.propTypes = {
  isError: PropTypes.bool,
  printResult: PropTypes.shape({
    successCount: PropTypes.number.isRequired,
    failedCount: PropTypes.number.isRequired,
    results: PropTypes.arrayOf(
      PropTypes.shape({
        lineItemId: PropTypes.string.isRequired,
        orderId: PropTypes.string,
        rxNumber: PropTypes.string,
        success: PropTypes.bool.isRequired,
        reason: PropTypes.string,
      })
    ).isRequired,
  }),
};

PrintResultSummary.defaultProps = {
  isError: false,
  printResult: null,
};

export default PrintResultSummary;
